/**
 * A design's quote-page views at a glance, for the Designs & leads list and the Designer's design list (pure,
 * tested with node --test). The API's `Lead.views` sums the homeowner's views of the project's leads (they follow
 * the chosen option); the team's own opens are previews, shown only in the tooltip and never counted.
 */
import { timeAgo } from "./format.ts";
import { type ViewsLike, when } from "./emailActivity.ts";
import type { Tr } from "./tr.ts";

/** "now", "5 min. ago", "2 hr. ago", "3 days ago", then the date (Intl, in `locale`). */
export function ago(value: string, now: Date = new Date(), locale?: string): string {
  return timeAgo(value, now, locale);
}

export type ViewState = { viewed: boolean; label: string; title: string };

/** "Viewed 3× · 2 h ago" or "Not opened yet"; null when no lead of the design has a quote link. */
export function viewState(v: ViewsLike | null | undefined, t: Tr, now: Date = new Date(), locale?: string): ViewState | null {
  if (!v) return null;
  const team = (v.member_preview_count ?? 0) + (v.network_preview_count ?? 0);
  const teamNote = team ? t("designs.views.teamNote", { count: team }) : "";
  if (!v.view_count)
    return { viewed: false, label: t("designs.views.notOpened"), title: [t("designs.views.notOpenedTitle"), teamNote].filter(Boolean).join(" ") };
  const last = v.last_viewed_at ?? v.first_viewed_at;
  const label = last ? t("designs.views.viewedAgo", { count: v.view_count, ago: ago(last, now, locale) }) : t("designs.views.viewed", { count: v.view_count });
  const first = when(v.first_viewed_at, now, locale);
  const lastAt = when(last, now, locale);
  const opened = !first
    ? t("designs.views.openedTitle", { count: v.view_count })
    : v.view_count > 1 && lastAt && lastAt !== first
      ? t("designs.views.openedTitleFirstLast", { count: v.view_count, first, last: lastAt })
      : t("designs.views.openedTitleAt", { count: v.view_count, at: first });
  return { viewed: true, label, title: [opened, teamNote].filter(Boolean).join(" ") };
}
